import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { THEME_STORAGE_KEY } from 'constants/theme';
import {
  applyThemeToDocument, effectiveTheme, isThemePreference, storeThemePreference, watchSystemTheme,
} from 'helper/theme';
import {
  selectResolvedTheme, selectThemePreference, setSystemPrefersDark, setThemePreference,
} from 'redux/reducers/themeReducer';

/**
 * Mantém o `<html data-bs-theme>` em dia com a preferência, o tema do sistema e a rota.
 * Montado uma vez, no `Layout` raiz. Ao desmontar, volta para o claro.
 *
 * `anyRoute`: para telas fora do Layout que já têm modo escuro em qualquer URL
 * (`/forbidden` e o 404, que responde por qualquer endereço desconhecido).
 */
export default function useThemeController(
  { anyRoute = false }: { anyRoute?: boolean } = {},
): void {
  const dispatch = useDispatch();
  const preference = useSelector(selectThemePreference);
  const resolved = useSelector(selectResolvedTheme);
  const { pathname } = useLocation();

  useEffect(() => watchSystemTheme((prefersDark) => {
    dispatch(setSystemPrefersDark(prefersDark));
  }), [dispatch]);

  // Outra aba trocou o tema: acompanha.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY && isThemePreference(event.newValue)) {
        dispatch(setThemePreference(event.newValue));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);

  useEffect(() => {
    storeThemePreference(preference);
  }, [preference]);

  useEffect(() => {
    applyThemeToDocument(anyRoute ? resolved : effectiveTheme(resolved, pathname));
  }, [anyRoute, resolved, pathname]);

  useEffect(() => () => applyThemeToDocument('light'), []);
}
